import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Grid,
  Container,
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { conversationService, botService } from '../services/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Message {
  id: number;
  message: string;
  response?: string;
  sender: string;
  recipient: string;
  timestamp: string;
  is_from_bot: boolean;
  is_manual_response: boolean;
}

interface Conversation {
  id: number;
  bot_id: number;
  phone_number: string;
  contact_name?: string;
  is_active: boolean;
  is_manual_mode: boolean;
  created_at: string;
  last_message?: Message;
  profile_pic_url?: string;
  unread_count?: number;
  last_message_at?: string;
}

const Chat: React.FC = () => {
  const { botId } = useParams<{ botId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  // Buscar bot (removido - não usado)

  // Buscar conversas
  const { data: conversations, isLoading: loadingConversations } = useQuery(
    ['conversations', botId],
    () => conversationService.getConversations(botId!),
    {
      enabled: !!botId,
      refetchInterval: 5000, // Atualizar a cada 5 segundos
    }
  );

  // Buscar mensagens da conversa selecionada
  const { data: messages, isLoading: loadingMessages } = useQuery(
    ['messages', selectedConversation?.id],
    () => conversationService.getMessages(String(selectedConversation!.id)),
    {
      enabled: !!selectedConversation,
      refetchInterval: 2000, // Atualizar a cada 2 segundos
    }
  );

  // Enviar mensagem
  const sendMessageMutation = useMutation(
    (messageText: string) =>
      conversationService.sendMessage(String(selectedConversation!.id), messageText),
    {
      onSuccess: () => {
        setMessage('');
        queryClient.invalidateQueries(['messages', selectedConversation?.id]);
      },
    }
  );

  // Alternar modo manual
  const toggleManualModeMutation = useMutation(
    (isManual: boolean) =>
      conversationService.toggleManualMode(String(selectedConversation!.id), isManual),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['conversations', botId]);
        queryClient.invalidateQueries(['messages', selectedConversation?.id]);
        // Atualizar a conversa selecionada com os novos dados
        if (selectedConversation) {
          setSelectedConversation({
            ...selectedConversation,
            is_manual_mode: data.data.is_manual_mode
          });
        }
      },
    }
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && selectedConversation) {
      sendMessageMutation.mutate(message);
    }
  };

  const handleSelectConversation = (conv: Conversation) => {
    setSelectedConversation(conv);
    // Marcar mensagens como lidas
    if (conv.unread_count && conv.unread_count > 0) {
      conversationService.getConversation(String(conv.id)).then(() => {
        queryClient.invalidateQueries(['conversations', botId]);
      });
    }
  };

  const formatPhoneNumber = (phone: string) => {
    // Formato: +55 11 98765-4321
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 13) {
      return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
    }
    return phone;
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/bots/${botId}`)}
        >
          Voltar para o Bot
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Lista de Conversas */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">Conversas</Typography>
            </Box>
            <List sx={{ flex: 1, overflow: 'auto' }}>
              {loadingConversations ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : conversations?.data?.length === 0 ? (
                <Box p={3}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Nenhuma conversa ainda
                  </Typography>
                </Box>
              ) : (
                conversations?.data?.map((conv: any) => (
                  <ListItem
                    key={conv.id}
                    button
                    selected={selectedConversation?.id === conv.id}
                    onClick={() => handleSelectConversation(conv)}
                  >
                    <ListItemAvatar>
                      <Avatar src={conv.profile_pic_url}>
                        {!conv.profile_pic_url && <PersonIcon />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            {conv.contact_name && (
                              <Typography variant="subtitle1">
                                {conv.contact_name}
                              </Typography>
                            )}
                            <Typography 
                              variant={conv.contact_name ? "body2" : "subtitle1"} 
                              color={conv.contact_name ? "text.secondary" : "text.primary"}
                            >
                              {formatPhoneNumber(conv.phone_number)}
                            </Typography>
                          </Box>
                          <Box display="flex" flexDirection="column" alignItems="flex-end">
                            {conv.last_message_at && (
                              <Typography variant="caption" color="text.secondary">
                                {format(new Date(conv.last_message_at), "dd/MM HH:mm", { locale: ptBR })}
                              </Typography>
                            )}
                            {conv.unread_count > 0 && (
                              <Chip 
                                label={conv.unread_count} 
                                size="small" 
                                color="primary"
                                sx={{ height: 20, mt: 0.5 }}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '250px',
                              flex: 1
                            }}
                          >
                            {conv.last_message || 'Sem mensagens'}
                          </Typography>
                          {conv.is_manual_mode && (
                            <Chip 
                              label="Manual" 
                              size="small" 
                              color="warning"
                              sx={{ height: 20 }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>

        {/* Área de Chat */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
            {selectedConversation ? (
              <>
                {/* Cabeçalho da Conversa */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar src={selectedConversation.profile_pic_url} sx={{ width: 40, height: 40 }}>
                        {!selectedConversation.profile_pic_url && <PersonIcon />}
                      </Avatar>
                      <Box>
                        {selectedConversation.contact_name && (
                          <Typography variant="h6">
                            {selectedConversation.contact_name}
                          </Typography>
                        )}
                        <Typography 
                          variant={selectedConversation.contact_name ? "body2" : "h6"} 
                          color={selectedConversation.contact_name ? "text.secondary" : "text.primary"}
                        >
                          {formatPhoneNumber(selectedConversation.phone_number)}
                        </Typography>
                      </Box>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={selectedConversation.is_manual_mode}
                          onChange={(e) => toggleManualModeMutation.mutate(e.target.checked)}
                          color="warning"
                        />
                      }
                      label="Modo Manual"
                    />
                  </Box>
                  {selectedConversation.is_manual_mode && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Modo manual ativo - O bot não responderá automaticamente
                    </Alert>
                  )}
                </Box>

                {/* Mensagens */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  {loadingMessages ? (
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <List>
                      {messages?.data?.map((msg: Message) => {
                        // Determinar se é mensagem enviada (bot ou atendente)
                        const isSentMessage = msg.is_from_bot || msg.is_manual_response;
                        const isAttendant = msg.is_manual_response && !msg.is_from_bot;
                        
                        return (
                          <ListItem
                            key={msg.id}
                            sx={{
                              justifyContent: isSentMessage ? 'flex-end' : 'flex-start',
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                maxWidth: '70%',
                                backgroundColor: isSentMessage 
                                  ? (isAttendant ? 'info.main' : '#03ba48')
                                  : 'grey.200',
                                color: isSentMessage ? 'white' : 'text.primary',
                                borderRadius: 2,
                                p: 2,
                                position: 'relative',
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                {isSentMessage ? (
                                  isAttendant ? (
                                    <>
                                      <PersonIcon fontSize="small" />
                                      <Typography variant="caption">Atendente</Typography>
                                    </>
                                  ) : (
                                    <>
                                      <BotIcon fontSize="small" />
                                      <Typography variant="caption">Bot</Typography>
                                    </>
                                  )
                                ) : (
                                  <>
                                    <PersonIcon fontSize="small" />
                                    <Typography variant="caption">Cliente</Typography>
                                  </>
                                )}
                              </Box>
                              <Typography variant="body1">{msg.message || msg.response}</Typography>
                              <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8 }}>
                                {format(new Date(msg.timestamp), "dd/MM HH:mm", { locale: ptBR })}
                              </Typography>
                            </Box>
                          </ListItem>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </List>
                  )}
                </Box>

                {/* Campo de Envio */}
                {selectedConversation.is_manual_mode && (
                  <Box
                    component="form"
                    onSubmit={handleSendMessage}
                    sx={{
                      p: 2,
                      borderTop: 1,
                      borderColor: 'divider',
                      display: 'flex',
                      gap: 1,
                    }}
                  >
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Digite sua mensagem..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      disabled={sendMessageMutation.isLoading}
                    />
                    <IconButton
                      type="submit"
                      color="primary"
                      disabled={!message.trim() || sendMessageMutation.isLoading}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                )}
              </>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography variant="body1" color="text.secondary">
                  Selecione uma conversa para visualizar
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Chat;